import { useEffect, useState, useCallback, useMemo, useContext } from 'react';
import { makeRequest, stringifyParams, getCanceler } from './request';
import { Query, Mutation, QueryResult, ResultM } from './types';
import { AuthContext } from 'auth/context';

export interface QueryConfig {
  skip?: boolean;
  authToken?: string;
}

const defaultConfig: QueryConfig = {
  skip: false
};

export const useQuery = <T>(
  q: Query<T>,
  config?: QueryConfig
): QueryResult<T> => {
  const [invalidator, setInvalidator] = useState(true);
  const refetch = useCallback(() => setInvalidator(x => !x), []);
  const [result, setResult] = useState<QueryResult<T>>({
    loading: true,
    refetch,
    skipped: false
  });
  let { token: authToken } = useContext(AuthContext);
  authToken = config?.authToken || authToken;

  const url = q.url + stringifyParams(q.params);
  const { skip } = { ...defaultConfig, ...config };
  useEffect(() => {
    if (skip) {
      setResult({ skipped: true, refetch, loading: false });
      return;
    }

    const { cancel, token: cancelToken } = getCanceler();

    setResult(r => ({ ...r, loading: true }));
    makeRequest<T>({ url }, { cancelToken, authToken })
      .then(res =>
        setResult({ data: res, loading: false, refetch, skipped: false })
      )
      .catch(error => {
        if (!error.canceled) {
          setResult({
            error: error.response,
            loading: false,
            refetch,
            skipped: false
          });
        }
      });
    return cancel;
  }, [url, invalidator, refetch, skip, authToken]);

  return result;
};

export const useMutation = <T extends (...args: any[]) => Mutation<any>>(
  mutationFn: T
): [(...args: Parameters<T>) => Promise<ResultM<T>>, ResultM<T>] => {
  const [result, setResult] = useState<ResultM<T>>({
    loading: false
  });
  const { cancel, token: cancelToken } = useMemo(getCanceler, [mutationFn]);
  const { token: authToken } = useContext(AuthContext);

  const mutate = useCallback(
    async (...args: Parameters<T>): Promise<ResultM<T>> => {
      setResult({ loading: true });
      const mutation = mutationFn(...args);
      try {
        const res = await makeRequest(mutation, { cancelToken, authToken });
        setResult({ data: res, loading: false });
        return { loading: false, data: res };
      } catch (error) {
        if (!error.canceled) {
          setResult({ error: error, loading: false });
        }
        return { loading: false, error: error };
      }
    },
    [mutationFn, cancelToken, authToken]
  );

  useEffect(() => cancel, [mutationFn, cancel]);

  return [mutate, result];
};
